import { FC, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";
import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import {
  AudioRecordingsSearchForm,
  AudioRecordingsSearchParams,
} from "@/pages/audio-recordings/root/audio-recordings-search-form";
import { AudioRecordingsTable } from "@/pages/audio-recordings/root/audio-recordings-table";
import {
  searchAudioRecordingsInternal,
  AudioRecordingSummaryInternalResponseSchema,
  AudioRecordingUploadStatus,
  AudioRecordingStatus,
  AudioRecordingProcessingStatus,
} from "@/pages/audio-recordings/shared/api";
import { validationModule } from "@/modules/validation";

const PAGE_SIZE = 50;

export const AudioRecordingsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL query parameters
  const initialParams: AudioRecordingsSearchParams = {
    query: searchParams.get("query") ?? "",
    spaceAccountId: searchParams.get("spaceAccountId") ?? "",
    uploadStatus: (searchParams.get("uploadStatus") ?? "") as AudioRecordingUploadStatus | "",
    recordingStatus: (searchParams.get("recordingStatus") ?? "") as AudioRecordingStatus | "",
    processingStatus: (searchParams.get("processingStatus") ?? "") as
      | AudioRecordingProcessingStatus
      | "",
  };

  const [searchState, setSearchState] = useState<AudioRecordingsSearchParams>(initialParams);
  const [offset, setOffset] = useState(0);
  const [allResults, setAllResults] = useState<AudioRecordingSummaryInternalResponseSchema[]>([]);

  // Determine if we have any active filters
  const hasActiveFilters =
    searchState.query ||
    searchState.spaceAccountId ||
    searchState.uploadStatus ||
    searchState.recordingStatus ||
    searchState.processingStatus;

  // React Query for search
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["audio-recordings-search", searchState, offset],
    queryFn: () =>
      searchAudioRecordingsInternal({
        query: searchState.query || undefined,
        filter_by_space_account_id:
          searchState.spaceAccountId && validationModule.isUuid(searchState.spaceAccountId)
            ? searchState.spaceAccountId
            : undefined,
        filter_by_upload_status: searchState.uploadStatus || undefined,
        filter_by_recording_status: searchState.recordingStatus || undefined,
        filter_by_processing_status: searchState.processingStatus || undefined,
        include_trashed: false,
        limit: PAGE_SIZE,
        offset: offset,
      }),
    enabled: Boolean(hasActiveFilters),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Reset results when search params change
  useEffect(() => {
    setOffset(0);
    setAllResults([]);
  }, [
    searchState.query,
    searchState.spaceAccountId,
    searchState.uploadStatus,
    searchState.recordingStatus,
    searchState.processingStatus,
  ]);

  // Accumulate results for pagination
  useEffect(() => {
    if (data?.results) {
      if (offset === 0) {
        setAllResults(data.results);
      } else {
        setAllResults(prev => [...prev, ...data.results]);
      }
    }
  }, [data, offset]);

  const handleViewAudioRecording = (id: string) => {
    navigate(atlasRoutes.audioRecordingsView({ params: { audioRecordingId: id } }).path);
  };

  const handleSearch = (params: AudioRecordingsSearchParams) => {
    // Update URL with search params
    const newSearchParams = new URLSearchParams();
    if (params.query) newSearchParams.set("query", params.query);
    if (params.spaceAccountId) newSearchParams.set("spaceAccountId", params.spaceAccountId);
    if (params.uploadStatus) newSearchParams.set("uploadStatus", params.uploadStatus);
    if (params.recordingStatus) newSearchParams.set("recordingStatus", params.recordingStatus);
    if (params.processingStatus) newSearchParams.set("processingStatus", params.processingStatus);
    setSearchParams(newSearchParams);
    setSearchState(params);
  };

  const handleLoadMore = () => {
    setOffset(prev => prev + PAGE_SIZE);
  };

  const total = data?.total ?? 0;
  const hasMore = allResults.length < total;

  return (
    <Container>
      <Header>
        <Title>Audio Recordings</Title>
        <DescriptionText>
          Search and filter audio recordings. Use the form below to find recordings by ID, title,
          space account, or status.
        </DescriptionText>
        <ButtonRow>
          <Button
            type="button"
            variant="solid"
            onClick={() => navigate(atlasRoutes.audioRecordingsQualityMetrics({}).path)}
          >
            View Quality Metrics
          </Button>
          <Button
            type="button"
            onClick={() => navigate(atlasRoutes.audioRecordingsCreate({}).path)}
          >
            Create New Recording
          </Button>
        </ButtonRow>
      </Header>

      <AudioRecordingsSearchForm initialValues={searchState} onSearch={handleSearch} />

      {isLoading && <LoadingMessage>Loading...</LoadingMessage>}
      {isError && (
        <ErrorMessage>Error: {error?.message ?? "Failed to load recordings"}</ErrorMessage>
      )}

      {hasActiveFilters && !isLoading && (
        <ResultsInfo>
          Showing {allResults.length} of {total} recordings
        </ResultsInfo>
      )}

      {allResults.length > 0 ? (
        <AudioRecordingsTable
          data={allResults}
          onViewAudioRecording={handleViewAudioRecording}
          pageSize={PAGE_SIZE}
          onLoadMore={handleLoadMore}
          hasMore={hasMore && !isFetching}
        />
      ) : (
        !isLoading &&
        hasActiveFilters && (
          <NoResultsMessage>No audio recordings found matching your criteria.</NoResultsMessage>
        )
      )}

      {!hasActiveFilters && (
        <HelpText>
          Enter search criteria above to find audio recordings. You can search by recording ID,
          title, space account ID, or filter by status.
        </HelpText>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 32px;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin: 0 0 8px 0;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-11);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const ResultsInfo = styled.div`
  font-size: 14px;
  color: var(--gray-11);
  margin-bottom: 12px;
`;

const LoadingMessage = styled.div`
  padding: 16px 0;
  color: var(--gray-11);
`;

const ErrorMessage = styled.div`
  padding: 16px;
  color: var(--red-11);
  background: var(--red-3);
  border-radius: 8px;
  margin-bottom: 16px;
`;

const NoResultsMessage = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--gray-11);
  background: var(--gray-2);
  border-radius: 8px;
`;

const HelpText = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--gray-10);
  background: var(--gray-2);
  border-radius: 8px;
  font-size: 14px;
`;
